import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { useAgent } from "@/api/queries/agents";
import { useAgentConfig } from "@/api/queries/agentConfig";
import {
  useReplaceManaged,
  usePatchManaged,
  usePatchOwner,
} from "@/api/mutations/agentConfig";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { BackLink } from "@/components/DetailHeader";
import { ApiError } from "@/api/client";
import { ConfigSection } from "@/components/agent-config/ConfigSection";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export function AgentConfig() {
  const { id } = useParams<{ id: string }>();
  const agentQuery = useAgent(id);
  const configQuery = useAgentConfig(id);

  const replaceManaged = useReplaceManaged();
  const patchManaged = usePatchManaged();
  const patchOwner = usePatchOwner();

  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replacePayload, setReplacePayload] = useState<Record<string, unknown> | null>(null);
  const [replaceError, setReplaceError] = useState<string | null>(null);

  const errorMessage =
    configQuery.error instanceof ApiError && configQuery.error.status === 404
      ? `Agent "${id}" nicht gefunden`
      : (configQuery.error as Error | undefined)?.message;

  if (!id) return null;

  function makeSaveHandler(
    mutation: typeof patchManaged | typeof patchOwner,
    layerName: string,
  ) {
    return (payload: Record<string, unknown>) => {
      mutation.mutate(
        { agentId: id!, payload },
        {
          onSuccess: () => toast.success(`${layerName} aktualisiert`),
          onError: (err) => toast.error(`${layerName} fehlgeschlagen: ${(err as Error).message}`),
        },
      );
    };
  }

  function handleReplaceClick(payload: Record<string, unknown>) {
    setReplacePayload(payload);
    setReplaceError(null);
    setReplaceOpen(true);
  }

  function handleReplaceConfirm() {
    if (!replacePayload || !id) return;
    setReplaceError(null);
    replaceManaged.mutate(
      { agentId: id, payload: replacePayload },
      {
        onSuccess: () => {
          toast.success("Managed-Layer ersetzt");
          setReplaceOpen(false);
          setReplacePayload(null);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setReplaceError(msg);
          toast.error(`Replace fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <div className="space-y-5">
      <BackLink to={`/agents/${id}`} label={agentQuery.data?.name ?? "Agent"} />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="m-0 text-[21px] font-extrabold tracking-tight text-text">
            Config: {agentQuery.data?.name ?? id}
          </h1>
          <p className="mt-1 max-w-[640px] text-[13px] text-text2">
            Settings: managed (admin) + owner (User). Brain-Container picked die zusammengeführte
            Config beim nächsten Launch. (#215: MCP läuft über das Workspace-Repo, nicht mehr hier.)
          </p>
        </div>
        {configQuery.data && (
          <Badge variant="default" data-testid="config-version">v{configQuery.data.version}</Badge>
        )}
      </div>

      {configQuery.isLoading && (
        <SectionCard testId="config-loading">
          <div className="flex items-center gap-3 py-8 text-sm text-text3">
            <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Config…
          </div>
        </SectionCard>
      )}

      {configQuery.isError && !configQuery.isLoading && (
        <SectionCard testId="config-error">
          <div className="flex items-start gap-3 py-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-danger" />
            <div className="flex-1">
              <div className="font-medium text-danger">Konnte nicht laden</div>
              <div className="mt-1 text-sm text-text3">{errorMessage}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => configQuery.refetch()}>
              <RefreshCw className="h-4 w-4" />Retry
            </Button>
          </div>
        </SectionCard>
      )}

      {configQuery.data && (
        <div className="grid gap-[18px] xl:grid-cols-2">
          <ConfigSection
            title="Managed"
            modeLabel="admin"
            value={configQuery.data.managed}
            isPending={patchManaged.isPending || replaceManaged.isPending}
            onSave={makeSaveHandler(patchManaged, "Managed")}
            onReplace={handleReplaceClick}
          />
          <ConfigSection
            title="Owner"
            modeLabel="owner-self"
            value={configQuery.data.owner}
            isPending={patchOwner.isPending}
            onSave={makeSaveHandler(patchOwner, "Owner")}
          />
        </div>
      )}

      <ConfirmDialog
        open={replaceOpen}
        onOpenChange={(o) => {
          if (!o) {
            setReplaceOpen(false);
            setReplacePayload(null);
            setReplaceError(null);
          }
        }}
        title="Managed-Layer ersetzen"
        description={
          <>
            Diese Aktion ersetzt die <strong>komplette managed-Ebene</strong>.
            Alle bisherigen Keys gehen verloren. Bist du sicher?
          </>
        }
        confirmText="Ersetzen"
        confirmVariant="destructive"
        isPending={replaceManaged.isPending}
        errorMessage={replaceError ?? undefined}
        onConfirm={handleReplaceConfirm}
      />
    </div>
  );
}
